/**
 * DettaglioCanone — il canone mensile di un'azienda voce per voce, IVA esclusa (02/11/2026).
 *
 * Lo stesso disegno in due posti: dentro il riquadro che si apre passando sopra la cifra dell'elenco delle aziende
 * (`compatto`) e nella scheda «Billing & Stripe». I numeri li calcola la regola condivisa
 * `@shared/abbonamento/canoneMensile`: qui si scrivono e basta.
 *
 * Due gruppi, perché sono due cose diverse: i POSTI (chi lavora nel gestionale) e i SERVIZI (app a persona e strumenti
 * a canone). Accanto a uno strumento si scrive come lo paga solo quando non è il listino: «prezzo suo», «regalo»,
 * «solo a consumo».
 */
import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { tokens } from '../../theme';
import { euro, ultimoGiorno } from '../strumenti/testiStrumenti';
import type {
  CanoneMensile, CondizioniDelloStrumento, IncassoDellAzienda, RigaDelCanone,
} from '@shared/abbonamento/canoneMensile';

const CONDIZIONI: Record<CondizioniDelloStrumento, string | null> = {
  listino: null,
  prezzo_proprio: 'prezzo suo',
  regalo: 'regalo',
  solo_consumo: 'solo a consumo',
};

/**
 * Perché il canone non entra (ancora) in cassa, in poche parole; null quando entra (`paga`) o non si paga per decisione
 * (`esente`). Una prova che finisce alla mezzanotte del 1° gennaio dura «fino al 31 dic».
 */
export function testoIncasso(incasso: IncassoDellAzienda): string | null {
  switch (incasso.stato) {
    case 'in_prova': return `in prova fino al ${ultimoGiorno(incasso.fino_al_ms)}`;
    case 'in_ritardo': return 'pagamento in ritardo';
    case 'in_pausa': return 'abbonamento in pausa';
    case 'senza_abbonamento': return 'nessun abbonamento';
    case 'paga':
    case 'esente':
    case 'dimostrativa':
      return null;
  }
}

/** «824 €», «30,50 €»: il canone è in centesimi. */
export const euroDaCentesimi = (c: number): string => euro(c / 100);

function dettaglio(r: RigaDelCanone): string | null {
  if (r.famiglia === 'strumento') return CONDIZIONI[r.condizioni];
  return `${r.quantita} × ${euroDaCentesimi(r.prezzo_centesimi)}`;
}

const Riga: React.FC<{ r: RigaDelCanone; corpo: string }> = ({ r, corpo }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto auto', columnGap: 2, alignItems: 'baseline', py: 0.25 }}>
    <Typography sx={{ fontSize: corpo, color: tokens.color.graphite, minWidth: 0 }} noWrap>
      {r.etichetta}
    </Typography>
    <Typography sx={{ fontSize: corpo, color: tokens.color.mist, fontVariantNumeric: 'tabular-nums' }}>
      {dettaglio(r) ?? ''}
    </Typography>
    <Typography
      sx={{
        fontFamily: tokens.font.mono, fontSize: corpo, textAlign: 'right', minWidth: 64,
        color: r.importo_centesimi > 0 ? tokens.color.graphite : tokens.color.mist,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {euroDaCentesimi(r.importo_centesimi)}
    </Typography>
  </Box>
);

const Gruppo: React.FC<{ titolo: string; righe: RigaDelCanone[]; totale: number; corpo: string }> = ({ titolo, righe, totale, corpo }) =>
  righe.length === 0 ? null : (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.25 }}>
        <Typography sx={{ fontSize: corpo, fontWeight: 700, color: tokens.color.ash }}>{titolo}</Typography>
        <Typography sx={{ fontFamily: tokens.font.mono, fontSize: corpo, fontWeight: 700, color: tokens.color.ash, fontVariantNumeric: 'tabular-nums' }}>
          {euroDaCentesimi(totale)}
        </Typography>
      </Stack>
      <Box sx={{ pl: 1.5, borderLeft: `2px solid ${tokens.color.lineSoft}` }}>
        {righe.map((r) => <Riga key={`${r.famiglia}:${r.id}`} r={r} corpo={corpo} />)}
      </Box>
    </Box>
  );

interface Props {
  canone: CanoneMensile;
  /** Versione stretta per il riquadro dell'elenco: niente note in fondo. */
  compatto?: boolean;
}

export const DettaglioCanone: React.FC<Props> = ({ canone, compatto = false }) => {
  const posti = canone.righe.filter((r) => r.famiglia === 'posti');
  const servizi = canone.righe.filter((r) => r.famiglia !== 'posti');
  const corpo = compatto ? '0.76rem' : '0.85rem';

  return (
    <Stack spacing={compatto ? 1 : 1.5} sx={{ minWidth: compatto ? 340 : undefined }}>
      <Gruppo titolo="Posti" righe={posti} totale={canone.posti_centesimi} corpo={corpo} />
      <Gruppo titolo="Servizi" righe={servizi} totale={canone.aggiunte_centesimi} corpo={corpo} />
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        sx={{ pt: compatto ? 0.75 : 1.25, borderTop: `1px solid ${tokens.color.line}` }}
      >
        <Typography sx={{ fontSize: corpo, fontWeight: 700, color: tokens.color.graphite }}>Al mese, IVA esclusa</Typography>
        <Typography sx={{ fontFamily: tokens.font.mono, fontSize: compatto ? '0.85rem' : '1rem', fontWeight: 700, color: tokens.color.graphite, fontVariantNumeric: 'tabular-nums' }}>
          {euroDaCentesimi(canone.totale_centesimi)}
        </Typography>
      </Stack>
      {canone.sconto_percentuale !== null && (
        <Typography sx={{ fontSize: '0.74rem', color: tokens.color.warning }}>
          Sconto dell’abbonamento −{canone.sconto_percentuale}% non compreso: Stripe lo toglie in fattura.
        </Typography>
      )}
      {canone.senza_prezzo.length > 0 && (
        <Typography sx={{ fontSize: '0.74rem', color: tokens.color.warning }}>
          Senza prezzo a listino, contati zero: {canone.senza_prezzo.join(', ')}.
        </Typography>
      )}
      {!compatto && (
        <Typography sx={{ fontSize: '0.74rem', color: tokens.color.mist }}>
          I consumi oltre la quota si aggiungono in fattura a fine mese (linguetta «Strumenti»). L’IVA la calcola Stripe.
        </Typography>
      )}
    </Stack>
  );
};
